import { CloverIcon, RocketLaunchIcon } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "../../lib/api";
import { relativeTime } from "../../lib/format";
import { catalogStatusQuery } from "../../lib/queries";
import { useCan } from "../../lib/session";
import { Button } from "../ui/button";
import { ConfirmDialog } from "../ui/dialog";
import { cn } from "../../lib/cn";

/**
 * The console's heartbeat: is the live site showing what the database holds? Gold pips mean
 * edits are saved but not published yet; a green clover means the two agree.
 */
export function ReleaseStrip() {
  const canRead = useCan("catalog.read");
  const canPublish = useCan("catalog.publish");
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [shine, setShine] = useState(false);

  const status = useQuery({ ...catalogStatusQuery, enabled: canRead, refetchInterval: 60_000 });

  const publish = useMutation({
    mutationFn: () => api<unknown>("/admin/catalog/publish", { body: { note: null } }),
    onSuccess: async () => {
      setConfirming(false);
      setShine(true);
      window.setTimeout(() => setShine(false), 1200);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["catalog-status"] }),
        queryClient.invalidateQueries({ queryKey: ["catalog-releases"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
      ]);
      toast.success("Published — the site is rebuilding");
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (!canRead || !status.data) return null;

  const pending = status.data.changedSinceLastRelease;
  const lastRelease = status.data.lastRelease;
  const pips = Math.min(5, Math.max(1, Math.ceil(pending / 10)));

  return (
    <>
      <div
        className={cn(
          "rounded-full border px-2.5 py-1",
          "flex items-center gap-2.5",
          pending > 0 ? "border-gold-line bg-gold-bg" : "border-accent-line bg-accent-bg",
          shine && "release-shine",
        )}
      >
        {pending > 0 ? (
          <span className="flex items-center gap-1" aria-hidden>
            {Array.from({ length: pips }, (_, index) => (
              <span key={index} className="pip" />
            ))}
          </span>
        ) : (
          <CloverIcon weight="fill" className="text-accent size-3.5" aria-hidden />
        )}

        <span className="text-caption whitespace-nowrap">
          {pending > 0 ? (
            <>
              <span className="text-gold-text font-semibold">{pending}</span>
              <span className="text-gold-text/80"> not live</span>
            </>
          ) : (
            <span className="text-accent-text font-medium">Catalog in sync</span>
          )}
        </span>

        {lastRelease && (
          <span className="text-caption text-faint hidden lg:inline">
            last {relativeTime(lastRelease.createdAt)}
          </span>
        )}

        {pending > 0 &&
          (canPublish ? (
            <Button size="sm" variant="primary" onClick={() => setConfirming(true)}>
              <RocketLaunchIcon /> Publish
            </Button>
          ) : (
            <Link to="/publishing">
              <Button size="sm" variant="ghost">
                Review
              </Button>
            </Link>
          ))}
      </div>

      <ConfirmDialog
        open={confirming}
        title="Publish the catalog"
        message={`${pending} edit${pending === 1 ? "" : "s"} will go live. The site rebuilds from a snapshot of the published catalog — players see the new version once the build finishes.`}
        confirmLabel="Publish now"
        pending={publish.isPending}
        onConfirm={() => publish.mutate()}
        onClose={() => setConfirming(false)}
      />
    </>
  );
}
